import { conY } from '../../logica/acceso';
import { ErrorApi } from '../../api/problema';
import type { Miembro } from '../../api/ops/equipo';
import type { TarjetaAyuda } from '../../marco/cabecera';

/**
 * Textos de Equipo (1.9), Roles (1.11), Propiedad (1.12) y Tablets (1.13), tal cual del diseño
 * (`1.9-1.13-equipo-roles-propiedad-tablets.html`) salvo donde una decisión lo corrige: G217 (quien deja la propiedad
 * queda como Administrador, además de sus otros roles), G219 (retirar a quien iba a recibirla cancela la transferencia)
 * y G212 (sede opcional al emparejar).
 */
export type Apartado = 'miembros' | 'invitaciones' | 'roles' | 'tablets';

export const AYUDA: Record<Apartado | 'invitar' | 'datos' | 'rolesMiembro' | 'pin' | 'permisos' | 'rolNuevo', TarjetaAyuda[]> = {
    miembros: [
        { icono: 'users', titulo: 'Miembros', texto: 'Las personas que entran al panel o a las tablets de este negocio. Cada una con su propia cuenta.' },
        { icono: 'shield-check', titulo: 'Dos pasos', texto: 'Cada persona la activa en Mi cuenta. Es opcional, pero protege la cuenta si alguien sabe su contraseña.' },
        { icono: 'user-cog', titulo: 'Varios roles', texto: 'Una persona puede tener varios roles a la vez; tiene la suma de sus permisos.' },
        // G217: quien deja la propiedad queda como Administrador, además de sus otros roles.
        { icono: 'crown', titulo: 'Propiedad', texto: 'Solo hay un propietario. Si se la pasas a otra persona y la acepta, quedas como Administrador, además de tus otros roles.' },
    ],
    invitaciones: [
        { icono: 'mail', titulo: 'Invitaciones', texto: 'Le llega un email con un enlace que caduca a los 7 días. Al aceptarlo entra en el equipo con los roles que elegiste.' },
        { icono: 'rotate-ccw', titulo: 'Reenviar', texto: 'Si caducó o no le llegó, reenvíala: el enlace anterior deja de funcionar y el nuevo dura otros 7 días.' },
        { icono: 'ban', titulo: 'Revocar', texto: 'El enlace deja de funcionar al momento y la invitación queda como anulada.' },
    ],
    roles: [
        { icono: 'user-cog', titulo: 'Roles estándar', texto: 'Los del sector, con sus permisos ya puestos. No se cambian.' },
        { icono: 'copy', titulo: 'Roles propios', texto: 'Duplica uno estándar y quita o añade permisos. Por ejemplo, una taquilla que cobra pero no cancela.' },
        { icono: 'lock', titulo: 'Lo que no ves', texto: 'El panel oculta lo que tu rol no puede hacer, y el servidor lo comprueba siempre.' },
    ],
    tablets: [
        { icono: 'tablet-smartphone', titulo: 'Tablets', texto: 'Una tablet emparejada abre Sala, escáner y taquilla sin que nadie inicie sesión.' },
        { icono: 'key-round', titulo: 'PIN', texto: 'Cada persona entra en la tablet con su PIN de 4 a 6 cifras. Así queda quién hizo cada cosa.' },
        { icono: 'clock', titulo: 'Código', texto: 'Para emparejarla te damos un código de 6 números que caduca a los 10 minutos.' },
        { icono: 'unplug', titulo: 'Revocar', texto: 'La tablet se cierra al momento. Para volver a usarla hay que emparejarla otra vez.' },
    ],
    invitar: [
        { icono: 'mail', titulo: 'Invitar', texto: 'Le llega un email con un enlace para unirse con estos roles. Caduca a los 7 días.' },
        { icono: 'user-cog', titulo: 'Roles', texto: 'Marca uno o varios. La persona tiene la suma de los permisos de todos.' },
    ],
    datos: [
        { icono: 'user', titulo: 'Sus datos', texto: 'Nombre, email y contraseña los cambia cada persona desde Mi cuenta.' },
        { icono: 'crown', titulo: 'Propiedad', texto: 'Solo hay un propietario. Si se la pasas a otra persona y la acepta, quedas como Administrador, además de tus otros roles.' },
    ],
    rolesMiembro: [
        { icono: 'user-cog', titulo: 'Roles', texto: 'Marca uno o varios. La persona tiene la suma de los permisos de todos.' },
        { icono: 'crown', titulo: 'Propietario', texto: 'No se marca aquí: se transfiere desde Datos.' },
    ],
    pin: [
        { icono: 'key-round', titulo: 'PIN de tablet', texto: 'De 4 a 6 cifras. Lo elige cada persona en Mi cuenta y nadie más lo ve.' },
        { icono: 'lock', titulo: 'Bloqueo', texto: '5 fallos seguidos lo bloquean 15 minutos. Si lo ha olvidado, pídele uno nuevo: borramos el suyo y le avisamos para que elija otro.' },
    ],
    permisos: [
        { icono: 'lock', titulo: 'Permisos', texto: 'Cada acción del panel es un permiso. Un rol es un conjunto de permisos.' },
        { icono: 'user', titulo: 'Solo lo suyo', texto: 'El RRPP ve solo sus listas y el profesional solo sus citas. Con dos roles, gana el permiso más amplio.' },
    ],
    rolNuevo: [{ icono: 'copy', titulo: 'Rol nuevo', texto: 'Ponle un nombre y ajusta sus permisos. Aparece en Roles y al invitar.' }],
};

/** «Propietario», o los roles de la persona con «y» antes del último («Propietario y Encargado», G995). */
export function rolesDeMiembro(m: Pick<Miembro, 'is_owner' | 'roles'>): string {
    const nombres = [...(m.is_owner ? ['Propietario'] : []), ...m.roles.map((r) => r.name)];
    return nombres.length === 0 ? '—' : conY(nombres);
}

export function nombreDePila(nombre: string): string {
    return nombre.trim().split(/\s+/)[0] ?? nombre;
}

const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** «el miércoles 21 de octubre» (hora de Madrid, la del negocio en el prototipo). */
export function fechaLarga(iso: string): string {
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    return `el ${DIAS[f.getDay()] ?? ''} ${String(f.getDate())} de ${MESES[f.getMonth()] ?? ''}`;
}

/** «Abril de 2025». */
export function mesYAnio(iso: string): string {
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const mes = MESES[f.getMonth()] ?? '';
    return `${mes.charAt(0).toUpperCase()}${mes.slice(1)} de ${String(f.getFullYear())}`;
}

/** «el 4 de agosto». */
export function diaYMes(iso: string): string {
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : `el ${String(f.getDate())} de ${MESES[f.getMonth()] ?? ''}`;
}

/** «hace 2 días», «en 5 días»: para la línea de una invitación. */
export function enDias(iso: string, ahora: Date): { dias: number; texto: string } {
    const dias = Math.round((new Date(iso).getTime() - ahora.getTime()) / 86_400_000);
    if (dias === 0) {
        return { dias, texto: 'hoy' };
    }
    const n = Math.abs(dias);
    const unidad = n === 1 ? 'día' : 'días';
    return { dias, texto: dias > 0 ? `en ${String(n)} ${unidad}` : `hace ${String(n)} ${unidad}` };
}

/** Texto para un aviso breve de error de la API (su `detail` o su título). */
export function textoDeError(e: unknown, porDefecto: string): string {
    return e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : porDefecto;
}

/** 412 al guardar un rol propio: se vuelve a leer con getRole (como en Negocio, G228). */
export const TEXTO_CONFLICTO_ROL = 'Alguien ha cambiado este rol mientras tanto. Lo he vuelto a cargar: revisa y guarda otra vez.';

/** Largo mínimo de un motivo (`Reason` del contrato); el de retirar es opcional (G336). */
export const MOTIVO_MINIMO = 10;

/** Sin el bloque de tablets: `403 entitlement_required` (G222). */
export function esNoContratado(e: unknown): boolean {
    return e instanceof ErrorApi && e.codigo === 'entitlement_required';
}

/** ¿Sigue en el futuro esa fecha? (PIN bloqueado hasta…). */
export function enElFuturo(iso: string | null | undefined, ahora: number): boolean {
    return iso != null && new Date(iso).getTime() > ahora;
}
